import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import CloseIcon from '@mui/icons-material/Close';
import { AxiosError } from 'axios';
import Cookies from 'js-cookie';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { api } from '../../../../shared/services/api';
import {
  StyledTableCellBody,
  StyledTableCellHead,
} from '../styledTableCell';
import type { IExecutionLog } from '../../types';

interface ExecutionLogsModalProps {
  open: boolean;
  onClose: () => void;
  recordId: string | null;
}

const ExecutionLogsModal = ({ open, onClose, recordId }: ExecutionLogsModalProps) => {
  const token = Cookies.get('@token');
  const [logs, setLogs] = useState<IExecutionLog[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (open && recordId) {
      fetchLogs(recordId);
    }
    if (!open) {
      setLogs([]);
    }
  }, [open, recordId]);

  const fetchLogs = async (id: string) => {
    try {
      setIsLoading(true);
      const response = await api.get(`/ajudArchive/executionLogs/${id}`, {
        headers: { Authorization: 'Bearer ' + token },
      });
      setLogs(Array.isArray(response.data) ? response.data : []);
    } catch (err: unknown) {
      if (err instanceof AxiosError) {
        toast.error(err.response?.data?.message || 'Erro ao buscar logs');
      } else {
        toast.error('Erro inesperado');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Logs de Execução
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {isLoading ? (
          <Typography sx={{ py: 4, textAlign: 'center' }}>Carregando...</Typography>
        ) : logs.length === 0 ? (
          <Typography sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
            Nenhum log de execução encontrado.
          </Typography>
        ) : (
          <Paper sx={{ width: '100%', overflow: 'hidden', mt: 1 }}>
            <TableContainer sx={{ maxHeight: 400 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <StyledTableCellHead>Agência</StyledTableCellHead>
                    <StyledTableCellHead>Conta</StyledTableCellHead>
                    <StyledTableCellHead>Dígito</StyledTableCellHead>
                    <StyledTableCellHead align="right">Valor Bloqueado</StyledTableCellHead>
                    <StyledTableCellHead>Protocolo Legado</StyledTableCellHead>
                    <StyledTableCellHead>Processo</StyledTableCellHead>
                    <StyledTableCellHead>Data</StyledTableCellHead>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {logs.map((log, idx) => (
                    <TableRow hover key={`${log.legacy_protocol_id}-${idx}`}>
                      <StyledTableCellBody>{log.agency || '-'}</StyledTableCellBody>
                      <StyledTableCellBody>{log.account || '-'}</StyledTableCellBody>
                      <StyledTableCellBody>{log.digit || '-'}</StyledTableCellBody>
                      <StyledTableCellBody align="right">
                        {log.value_blocked != null
                          ? `R$ ${Number(log.value_blocked).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
                          : '-'}
                      </StyledTableCellBody>
                      <StyledTableCellBody>
                        <Box
                          component="span"
                          sx={{ fontFamily: 'monospace', fontSize: 12 }}
                        >
                          {log.legacy_protocol_id || '-'}
                        </Box>
                      </StyledTableCellBody>
                      <StyledTableCellBody>{log.court_case || '-'}</StyledTableCellBody>
                      <StyledTableCellBody>
                        {log.created_at
                          ? moment(log.created_at).format('DD/MM/YYYY HH:mm')
                          : '-'}
                      </StyledTableCellBody>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ExecutionLogsModal;
